import type * as THREE from 'three'
import type { SoundEffects } from '../audio/sfx'

export interface TutorialTrigger {
  id: string
  message: string
  position: { x: number; y: number; z: number }
  radius: number
  hasTriggered: boolean
}

export class TutorialHintSystem {
  private readonly container: HTMLElement
  private readonly triggers: TutorialTrigger[]

  private hideTimeout: number | null = null

  constructor(customTriggers?: TutorialTrigger[]) {
    this.triggers = customTriggers ?? [
      {
        id: 'spawn',
        message: 'COMM LINK ACTIVE: Welcome to Combat Training Facility. Proceed through blast doors.',
        position: { x: 0, y: 1.2, z: -1 },
        radius: 4.0,
        hasTriggered: false,
      },
      {
        id: 'launchdr',
        message: 'DROPSHIP IN POSITION: Training blast doors unlocked. Walk close to open.',
        position: { x: 0, y: 1.2, z: 1.5 },
        radius: 3.5,
        hasTriggered: false,
      },
      {
        id: 'target-range',
        message: 'WEAPONS RANGE: Take aim and fire at targets. Try right-click to zoom.',
        position: { x: -8, y: 1.2, z: -4 },
        radius: 4.5,
        hasTriggered: false,
      },
      {
        id: 'breakables',
        message: 'TACTICAL NOTICE: Some objects are breakable. Shoot crates to uncover supplies.',
        position: { x: -14, y: 1.2, z: -6 },
        radius: 5.0,
        hasTriggered: false,
      },
      {
        id: 'elevator',
        message: 'CIRCUIT SECTOR: Step onto the kinematic platform lift to ascend.',
        position: { x: 4, y: 1.2, z: -5 },
        radius: 4.0,
        hasTriggered: false,
      },
      {
        id: 'jump',
        message: 'AGILITY HINT: Hold SPACE (Jump) longer to maximize vertical booster height.',
        position: { x: -4, y: 1.2, z: 0 },
        radius: 3.5,
        hasTriggered: false,
      },
    ]

    let el = document.getElementById('tutorial-comms')
    if (!el) {
      el = document.createElement('div')
      el.id = 'tutorial-comms'
      el.style.position = 'fixed'
      el.style.top = '18%'
      el.style.left = '50%'
      el.style.transform = 'translateX(-50%)'
      el.style.padding = '12px 24px'
      el.style.background = 'rgba(10, 20, 30, 0.85)'
      el.style.border = '2px solid #00f0ff'
      el.style.boxShadow = '0 0 16px rgba(0, 240, 255, 0.5)'
      el.style.color = '#00f0ff'
      el.style.fontFamily = 'monospace'
      el.style.fontSize = '15px'
      el.style.letterSpacing = '1px'
      el.style.textTransform = 'uppercase'
      el.style.pointerEvents = 'none'
      el.style.zIndex = '999'
      el.style.transition = 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out'
      el.style.opacity = '0'
      document.body.appendChild(el)
    }
    this.container = el
  }

  show(text: string, sfx?: SoundEffects) {
    this.container.textContent = text
    this.container.style.opacity = '1'
    this.container.style.transform = 'translateX(-50%) translateY(0)'

    if (sfx) {
      sfx.playPickup()
    }

    if (this.hideTimeout !== null) {
      window.clearTimeout(this.hideTimeout)
    }
    this.hideTimeout = window.setTimeout(() => {
      this.container.style.opacity = '0'
      this.container.style.transform = 'translateX(-50%) translateY(-10px)'
    }, 6000)
  }

  update(playerPos: THREE.Vector3, sfx?: SoundEffects) {
    for (let i = 0; i < this.triggers.length; i++) {
      const trigger = this.triggers[i]
      if (trigger.hasTriggered) continue

      const dx = playerPos.x - trigger.position.x
      const dy = playerPos.y - trigger.position.y
      const dz = playerPos.z - trigger.position.z
      const distSq = dx * dx + dy * dy + dz * dz

      if (distSq <= trigger.radius * trigger.radius) {
        trigger.hasTriggered = true
        this.show(trigger.message, sfx)
        break
      }
    }
  }
}
